import { useEffect, useState, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { FileSpreadsheet, FileText, ScrollText, Search, X } from "lucide-react";
import { api, ApiError, type DownloadEvent, type DownloadEventSearch, type ViewAsAction, type ViewAsEvent, type ViewAsEventSearch } from "../../lib/api";
import { addDays, iso } from "../../lib/time";
import { formatRange, VIZ_SPECS } from "@cd/viz";
import { cn, useDebouncedValue } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge, Input, Select, Skeleton } from "../../components/ui/primitives";
import { ScheduleTable } from "../schedules/SchedulesPage";
import { AskUsageFilters, AskUsageLog } from "./AskUsage";
import { ContactLog } from "./ContactsAudit";

// Super users: every staff "view as" (the worker's viewAs.ts) and every widget download, newest
// first, every scheduled report email (read-only), Ask's tokens by month (AskUsage.tsx), and
// changes to external contacts and approved domains (ContactsAudit.tsx). The tab and filters live in the URL, so a filtered log (one staff member, one view) can be
// linked to.

const ACTIONS: Record<ViewAsAction, { label: string; tone: "neutral" | "info" | "warn" | "accent" }> = {
  start: { label: "Started", tone: "info" },
  enable_edit: { label: "Enabled editing", tone: "warn" },
  write: { label: "Change", tone: "accent" },
  exit: { label: "Exited", tone: "neutral" },
};

/** The page's URL search: text filters, enums, and inclusive calendar days (YYYY-MM-DD) in the viewer's time zone. */
export interface AuditSearch {
  tab?: "downloads" | "schedules" | "ask-usage" | "contacts";
  // Contacts: one contact's changes.
  contact?: string;
  // View as
  staff?: string;
  target?: string;
  view?: string;
  action?: ViewAsAction;
  tier?: "support" | "super";
  kind?: "customer" | "staff";
  // Downloads
  person?: string;
  by?: string;
  widget?: string;
  format?: DownloadEvent["format"];
  // Ask usage: calendar months (YYYY-MM, UTC), one org's people, and whether staff asks count.
  since?: string;
  until?: string;
  org?: string;
  staffAsks?: "include" | "only";
  // View as and downloads
  from?: string;
  to?: string;
}

const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const oneOf = <T extends string>(v: unknown, options: readonly T[]) => (options.includes(v as T) ? (v as T) : undefined);
const month = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}$/.test(v) ? v : undefined);
const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

export const parseAuditSearch = (s: Record<string, unknown>): AuditSearch => ({
  tab: oneOf(s.tab, ["downloads", "schedules", "ask-usage", "contacts"] as const),
  contact: text(s.contact),
  staff: text(s.staff),
  target: text(s.target),
  view: text(s.view),
  action: oneOf(s.action, Object.keys(ACTIONS) as ViewAsAction[]),
  tier: oneOf(s.tier, ["support", "super"] as const),
  kind: oneOf(s.kind, ["customer", "staff"] as const),
  person: text(s.person),
  by: text(s.by),
  widget: text(s.widget),
  format: oneOf(s.format, ["xlsx", "csv"] as const),
  since: month(s.since),
  until: month(s.until),
  org: text(s.org),
  staffAsks: oneOf(s.staffAsks, ["include", "only"] as const),
  from: day(s.from),
  to: day(s.to),
});

const startOfDay = (d: string) => new Date(`${d}T00:00:00`);

const apiRange = (s: AuditSearch) => ({
  from: s.from && startOfDay(s.from).toISOString(),
  to: s.to && addDays(startOfDay(s.to), 1).toISOString(),
});

function toApiSearch(s: AuditSearch): ViewAsEventSearch {
  return { staff: s.staff, target: s.target, viewAsId: s.view, action: s.action, tier: s.tier, targetKind: s.kind, ...apiRange(s) };
}

function toDownloadSearch(s: AuditSearch): DownloadEventSearch {
  return { user: s.person, actor: s.by, widgetId: s.widget, viewAsId: s.view, format: s.format, ...apiRange(s) };
}

export type SetSearch = (change: Partial<AuditSearch>) => void;

export function AuditLogPage() {
  const search = useSearch({ from: "/admin/audit" });
  const navigate = useNavigate({ from: "/admin/audit" });
  const set: SetSearch = (change) => void navigate({ search: (s) => ({ ...s, ...change }), replace: true });
  // Filters belong to one log; switching starts the other one unfiltered.
  const show = (tab: AuditSearch["tab"]) => void navigate({ search: { tab }, replace: true });
  const { tab, ...filters } = search;
  const filtered = Object.values(filters).some(Boolean);
  const clear = () => void navigate({ search: { tab }, replace: true });

  return (
    <div className="mx-auto max-w-[1400px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><ScrollText className="size-4 text-subtle" /> Audit log</h1>
          <div className="flex rounded-lg border border-border p-0.5" role="group" aria-label="Log">
            {([[undefined, "View as"], ["downloads", "Downloads"], ["schedules", "Schedules"], ["contacts", "Contacts"], ["ask-usage", "Ask usage"]] as const).map(([t, label]) => (
              <button
                key={label}
                onClick={() => show(t)}
                aria-pressed={tab === t}
                className={cn("rounded-md px-2.5 py-1 text-xs font-medium text-muted hover:text-foreground", tab === t && "bg-surface-2 text-foreground")}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-sm text-muted">
            {tab === "downloads"
              ? "Every widget downloaded as Excel or CSV, and by whom."
              : tab === "schedules"
                ? "Every scheduled report email in this workspace. Only its owner can change one."
                : tab === "ask-usage"
                  ? "Ask's tokens by calendar month (UTC) and org. Pick an org to see its people."
                  : tab === "contacts"
                    ? "Every external contact added, changed or deleted, and every approved domain added or removed."
                    : "Every time staff viewed as someone: starts, edit switches, changes and exits."}
          </span>
        </div>
        {tab !== "schedules" && tab !== "contacts" && <div className="mt-3 flex flex-wrap items-center gap-2">
          {tab === "ask-usage" ? <AskUsageFilters search={search} set={set} /> : <>
            {tab === "downloads" ? <DownloadFilters search={search} set={set} /> : <ViewAsFilters search={search} set={set} />}
            <label className="flex items-center gap-1.5 text-xs text-muted">
              From
              <Input type="date" value={search.from ?? ""} max={search.to ?? iso(new Date())} onChange={(e) => set({ from: e.target.value || undefined })} className="w-36" />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted">
              To
              <Input type="date" value={search.to ?? ""} min={search.from} onChange={(e) => set({ to: e.target.value || undefined })} className="w-36" />
            </label>
          </>}
          {search.view && (
            <Badge tone="accent" className="h-8 px-2 text-xs">
              One view · {search.view.slice(0, 8)}
              <button onClick={() => set({ view: undefined })} aria-label="Show all views" className="rounded hover:bg-accent/10"><X className="size-3" /></button>
            </Badge>
          )}
          {search.widget && (
            <Badge tone="accent" className="h-8 px-2 text-xs">
              One widget · {search.widget.slice(0, 8)}
              <button onClick={() => set({ widget: undefined })} aria-label="Show all widgets" className="rounded hover:bg-accent/10"><X className="size-3" /></button>
            </Badge>
          )}
          {filtered && <Button size="sm" variant="ghost" onClick={clear}>Clear filters</Button>}
        </div>}
      </header>

      {tab === "downloads"
        ? <DownloadLog search={search} set={set} filtered={filtered} />
        : tab === "schedules"
          ? <AllSchedules />
          : tab === "ask-usage"
            ? <AskUsageLog search={search} set={set} />
            : tab === "contacts"
              ? <ContactLog search={search} set={set} />
              : <ViewAsLog search={search} set={set} filtered={filtered} />}
    </div>
  );
}

function ViewAsFilters({ search, set }: { search: AuditSearch; set: SetSearch }) {
  return (
    <>
      <TextFilter value={search.staff} onChange={(staff) => set({ staff })} placeholder="Staff email or ID" label="Filter by staff member" />
      <TextFilter value={search.target} onChange={(target) => set({ target })} placeholder="Viewed as: name or user ID" label="Filter by person viewed" />
      <Select value={search.action ?? ""} onChange={(e) => set({ action: (e.target.value || undefined) as ViewAsAction | undefined })} aria-label="Action">
        <option value="">All actions</option>
        {Object.entries(ACTIONS).map(([id, a]) => <option key={id} value={id}>{a.label}</option>)}
      </Select>
      <Select value={search.kind ?? ""} onChange={(e) => set({ kind: (e.target.value || undefined) as AuditSearch["kind"] })} aria-label="Viewed as">
        <option value="">Customers and staff</option>
        <option value="customer">Customers</option>
        <option value="staff">Staff</option>
      </Select>
      <Select value={search.tier ?? ""} onChange={(e) => set({ tier: (e.target.value || undefined) as AuditSearch["tier"] })} aria-label="Tier">
        <option value="">Support and super users</option>
        <option value="support">Support</option>
        <option value="super">Super users</option>
      </Select>
    </>
  );
}

function DownloadFilters({ search, set }: { search: AuditSearch; set: SetSearch }) {
  return (
    <>
      <TextFilter value={search.person} onChange={(person) => set({ person })} placeholder="Person: name, email or ID" label="Filter by whose data" />
      <TextFilter value={search.by} onChange={(by) => set({ by })} placeholder="Signed in as: email or ID" label="Filter by who was signed in" />
      <Select value={search.format ?? ""} onChange={(e) => set({ format: (e.target.value || undefined) as AuditSearch["format"] })} aria-label="Format">
        <option value="">Excel and CSV</option>
        <option value="xlsx">Excel</option>
        <option value="csv">CSV</option>
      </Select>
    </>
  );
}

/** Loading, error, empty, the table and "Load more", for any of the logs. */
export function LogTable<T extends { id: string }>({ results, items, headers, empty, row }: {
  results: { isLoading: boolean; error: Error | null; hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown };
  items: T[];
  headers: string[];
  empty: string;
  row: (item: T) => ReactNode;
}) {
  return (
    <>
      {results.isLoading ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9" />)}</div>
      ) : results.error ? (
        <p className="text-sm text-bad">
          {results.error instanceof ApiError && results.error.status === 403 ? "The audit log is for super users only." : results.error.message}
        </p>
      ) : !items.length ? (
        <p className="mt-16 text-center text-sm text-muted">{empty}</p>
      ) : (
        <div className="overflow-x-auto rounded-[14px] border border-border bg-surface">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h} className="border-b border-border px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{items.map(row)}</tbody>
          </table>
        </div>
      )}
      {results.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button disabled={results.isFetchingNextPage} onClick={() => void results.fetchNextPage()}>Load more</Button>
        </div>
      )}
    </>
  );
}

function ViewAsLog({ search, set, filtered }: { search: AuditSearch; set: SetSearch; filtered: boolean }) {
  const results = useInfiniteQuery({
    queryKey: ["view-as-events", search],
    queryFn: ({ pageParam }) => api.viewAsEvents(toApiSearch(search), pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    retry: false,
  });
  return (
    <LogTable
      results={results}
      items={results.data?.pages.flatMap((p) => p.events) ?? []}
      headers={["When", "Staff", "Viewed as", "Action", "Detail", "View"]}
      empty={filtered ? "Nothing matches these filters." : "No one has viewed as anyone yet."}
      row={(e) => <EventRow key={e.id} e={e} onFilter={set} />}
    />
  );
}

function DownloadLog({ search, set, filtered }: { search: AuditSearch; set: SetSearch; filtered: boolean }) {
  const results = useInfiniteQuery({
    queryKey: ["downloads", search],
    queryFn: ({ pageParam }) => api.downloads(toDownloadSearch(search), pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    retry: false,
  });
  return (
    <LogTable
      results={results}
      items={results.data?.pages.flatMap((p) => p.events) ?? []}
      headers={["When", "Person", "Widget", "Format", "Data", "View"]}
      empty={filtered ? "Nothing matches these filters." : "No one has downloaded anything yet."}
      row={(e) => <DownloadRow key={e.id} e={e} onFilter={set} />}
    />
  );
}

function AllSchedules() {
  const schedules = useQuery({ queryKey: ["all-schedules"], queryFn: api.allSchedules, retry: false });
  return <ScheduleTable query={schedules} empty="No one has scheduled a report yet." forbidden="The audit log is for super users only." />;
}

/** A text filter that updates the URL once typing pauses. */
function TextFilter({ value, onChange, placeholder, label }: { value?: string; onChange: (v: string | undefined) => void; placeholder: string; label: string }) {
  const [draft, setDraft] = useState(value ?? "");
  const debounced = useDebouncedValue(draft.trim(), 300);
  // Follow the URL when it changes elsewhere (a clicked cell, Clear filters, back/forward).
  useEffect(() => setDraft(value ?? ""), [value]);
  useEffect(() => {
    if (debounced !== (value ?? "")) onChange(debounced || undefined);
    // Only when the typed value settles, not when the URL moves under it.
  }, [debounced]);
  return (
    <div className="relative w-56">
      <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} aria-label={label} className="pl-8" />
    </div>
  );
}

function EventRow({ e, onFilter }: { e: ViewAsEvent; onFilter: SetSearch }) {
  const a = ACTIONS[e.action];
  const cell = "border-b border-border px-3 py-2 align-top";
  return (
    <tr className="hover:bg-surface-2/60">
      <td className={cn(cell, "whitespace-nowrap tabular text-muted")} title={e.at}>
        {new Date(e.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" })}
      </td>
      <td className={cell}>
        <FilterLink onClick={() => onFilter({ staff: e.staffId })} title="Only this staff member">{e.staffEmail ?? e.staffId}</FilterLink>
        {e.tier === "super" && <Badge tone="accent" className="ml-1.5">Super</Badge>}
      </td>
      <td className={cell}>
        <FilterLink onClick={() => onFilter({ target: e.targetUserId })} title="Only views of this person">{e.targetName ?? e.targetUserId}</FilterLink>
        <span className="block text-xs text-subtle">{e.targetKind === "staff" ? "Staff" : "Customer"} · {e.targetUserId}</span>
      </td>
      <td className={cn(cell, "whitespace-nowrap")}><Badge tone={a.tone}>{a.label}</Badge></td>
      <td className={cn(cell, "min-w-64")}><Detail e={e} /></td>
      <td className={cell}>
        <FilterLink onClick={() => onFilter({ view: e.viewAsId })} title="Everything in this view" className="font-mono text-xs">{e.viewAsId.slice(0, 8)}</FilterLink>
      </td>
    </tr>
  );
}

const when = (at: string) => (
  <td className="border-b border-border px-3 py-2 align-top whitespace-nowrap tabular text-muted" title={at}>
    {new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" })}
  </td>
);

function DownloadRow({ e, onFilter }: { e: DownloadEvent; onFilter: SetSearch }) {
  const cell = "border-b border-border px-3 py-2 align-top";
  const n = e.detail?.locationIds?.length;
  const range = e.detail?.timeRange;
  return (
    <tr className="hover:bg-surface-2/60">
      {when(e.at)}
      <td className={cell}>
        <FilterLink onClick={() => onFilter({ person: e.userId })} title="Only this person's downloads">{e.userName ?? e.userEmail ?? e.userId}</FilterLink>
        {e.actorId !== e.userId && (
          <span className="block text-xs text-subtle">
            by <FilterLink onClick={() => onFilter({ by: e.actorId })} title="Only downloads this person made">{e.actorEmail ?? e.actorId}</FilterLink>, viewing as them
          </span>
        )}
      </td>
      <td className={cell}>
        <FilterLink onClick={() => onFilter({ widget: e.widgetId })} title="Only this widget">{e.widgetTitle || "Untitled"}</FilterLink>
        {e.detail?.vizType && <span className="block text-xs text-subtle">{VIZ_SPECS.find((v) => v.type === e.detail!.vizType)?.label ?? e.detail.vizType}</span>}
      </td>
      <td className={cn(cell, "whitespace-nowrap")}>
        <Badge tone="neutral">
          {e.format === "xlsx" ? <FileSpreadsheet className="size-3" /> : <FileText className="size-3" />}
          {e.format === "xlsx" ? "Excel" : "CSV"}
        </Badge>
      </td>
      <td className={cn(cell, "text-muted")}>
        {e.rowCount.toLocaleString()} row{e.rowCount === 1 ? "" : "s"}
        {range && ` · ${formatRange(range)}`}
        <span className="block text-xs text-subtle" title={e.detail?.locationIds?.join(", ")}>
          {n ? `${n} location${n === 1 ? "" : "s"}` : "All their locations"}
        </span>
      </td>
      <td className={cell}>
        {e.viewAsId ? (
          <FilterLink onClick={() => onFilter({ view: e.viewAsId! })} title="Downloads in this view" className="font-mono text-xs">{e.viewAsId.slice(0, 8)}</FilterLink>
        ) : <span className="text-subtle">—</span>}
      </td>
    </tr>
  );
}

export function FilterLink({ onClick, title, className, children }: { onClick: () => void; title: string; className?: string; children: ReactNode }) {
  return (
    <button onClick={onClick} title={title} className={cn("text-left hover:text-accent hover:underline", className)}>{children}</button>
  );
}

function Detail({ e }: { e: ViewAsEvent }) {
  const d = e.detail;
  switch (e.action) {
    case "start": {
      const n = d?.locationIds?.length ?? 0;
      return (
        <span className="text-muted" title={d?.locationIds?.join(", ")}>
          {n} location{n === 1 ? "" : "s"}
          {d?.expiresAt && ` · until ${new Date(d.expiresAt).toLocaleTimeString(undefined, { timeStyle: "short" })}`}
        </span>
      );
    }
    case "enable_edit":
      return <span className="whitespace-pre-wrap break-words">“{d?.reason}”</span>;
    case "write":
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <code className="font-mono text-xs">{d?.method} {d?.path}</code>
          {d?.status === undefined ? (
            <Badge tone="warn" title="Recorded before it ran; no response was recorded">No response</Badge>
          ) : (
            <Badge tone={d.status < 400 ? "good" : "warn"}>{d.status}</Badge>
          )}
        </span>
      );
    default:
      return <span className="text-subtle">—</span>;
  }
}
